'use client'

import { EyeIcon, EyeOffIcon, LockIcon, UserIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState, useTransition } from 'react'
import { signInWithPasswordAction, type SignInError } from '@/app/(auth)/sign-in/actions'
import { AuthSubmitButton } from '@/components/auth/auth-submit-button'
import { IconInput } from '@/components/auth/icon-input'
import { Button } from '@/components/ui/button'
import { Field, FieldError, FieldLabel } from '@/components/ui/field'

export function PasswordForm({ callbackUrl }: { callbackUrl?: string }) {
	const t = useTranslations('auth')
	const [error, setError] = useState<SignInError | null>(null)
	const [showPassword, setShowPassword] = useState(false)
	const [isPending, startTransition] = useTransition()

	const submit = (formData: FormData) => {
		setError(null)
		startTransition(async () => {
			const result = await signInWithPasswordAction({
				username: String(formData.get('username') ?? ''),
				password: String(formData.get('password') ?? ''),
				callbackUrl,
			})
			if (!result.ok) setError(result.error)
		})
	}

	return (
		<form className="flex flex-col gap-4" action={submit}>
			<Field>
				<FieldLabel htmlFor="username">{t('username.label')}</FieldLabel>
				<IconInput
					icon={UserIcon}
					id="username"
					name="username"
					autoComplete="username"
					required
					placeholder={t('username.placeholder')}
				/>
			</Field>
			<Field>
				<FieldLabel htmlFor="password">{t('password.label')}</FieldLabel>
				<IconInput
					icon={LockIcon}
					id="password"
					name="password"
					type={showPassword ? 'text' : 'password'}
					autoComplete="current-password"
					required
					trailing={
						<Button
							type="button"
							variant="ghost"
							size="icon-sm"
							aria-label={showPassword ? t('password.hide') : t('password.show')}
							onClick={() => setShowPassword((value) => !value)}
						>
							{showPassword ? <EyeOffIcon /> : <EyeIcon />}
						</Button>
					}
				/>
				{error && <FieldError>{t(`errors.${error}`)}</FieldError>}
			</Field>
			<AuthSubmitButton pending={isPending}>{t('password.submit')}</AuthSubmitButton>
		</form>
	)
}
